import { FC } from "react";
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@material-ui/icons";
import { Link } from "react-router-dom";

import { Button } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { BaseContainer } from "containers";
import * as routes from "routes";
import { useAppConfigs } from "services/appConfigs";
import theme, { Theme } from "theme";

const useStyles = makeStyles<
  Theme,
  { isXsDown: boolean; isSmDown: boolean; isMdDown: boolean; isMdUp: boolean }
>(() => ({
  header: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  backToDashboardButton: {
    paddingLeft: 0,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },
  subtitle: {
    fontSize: "1.8rem",
    fontWeight: 400,
    marginTop: theme.spacing(2),
  },
  card: props => ({
    background: theme.palette.background.default,
    padding: props.isMdDown ? theme.spacing(1.5) : theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    height: "100%",
  }),
  cardInfos: props => ({
    display: "flex",
    flexDirection: "column",
    minHeight: props.isSmDown && !props.isXsDown ? "unset" : "210px",
    marginBottom: theme.spacing(2),
  }),
  cardInfoText: props => ({
    textAlign: "left",
    height: "100%",
    marginTop: props.isMdDown && !props.isXsDown ? "unset" : "unset",
    marginBottom: props.isMdDown && !props.isXsDown ? "unset" : "unset",
  }),
  cardTitle: {
    fontSize: "1.6rem",
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  circleIcon: props => ({
    color: theme.custom.palette.green.main,
    fontSize: props.isSmDown ? "25px" : "30px",
  }),
  stepCard: {
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
    textAlign: "left",
    padding: theme.spacing(3),
  },
  link: {
    color: theme.custom.palette.purple.main,
    fontSize: "0.875rem",
    letterSpacing: "1.2px",
    opacity: 0.5,
    cursor: "pointer",
    "& .nextArrow": {
      fontSize: "0.75rem",
      transition: "transform 200ms",
    },
    "&:hover": {
      color: theme.custom.palette.purple.main,
      textDecoration: "none",
      opacity: 1,
      "& .nextArrow": {
        transform: "translateX(4px)",
      },
    },
  },
  arrowIcon: {
    fontSize: "0.75rem",
    verticalAlign: "middle",
    color: theme.custom.palette.purple.main,
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  popularBorder: {
    position: "relative",
    border: `2px solid ${theme.custom.palette.purple.main}`,
  },
  popularText: {
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    background: theme.custom.palette.purple.main,
    position: "absolute",
    right: "30px",
    top: "-15px",
    color: theme.custom.palette.softWhite.main,
  },
}));

const cardsContent = [
  {
    title: "Fixed Payback",
    subTitle:
      "Investors bid to receive the full catalog income until the full payback is recouped. Once the payback amount is fulfilled, 100% of the royalties revert back to you.",
    bullets: [
      "Short Term",
      "Lowest amount of money compared to longer terms, like Sale.",
      "You can benefit from significant capital infusion during term while retaining control of your catalog afterwards.",
    ],
  },
  {
    title: "Fixed Term",
    subTitle:
      "Investors make offers to collect the royalties for the duration of 10 years. After the 10 year period ends, the royalties revert back to you.",
    bullets: [
      "Medium Term",
      "More money than Fixed Payback but less than Sale.",
      "Allows you to raise capital quickly and retain ownership of your catalog.",
      "Suitable if you need immediate financial support.",
    ],
  },
  {
    title: "Sale",
    subTitle:
      "Investors purchase the rights to collect the royalties from the catalog for the entire duration of the copyright.",
    bullets: [
      "Long Term",
      "Higher amount of money compared to shorter terms, like Fixed Payback.",
      "Advantageous if you prioritize a significant financial return and are willing to part ways with future royalty income.",
    ],
  },
];

const makeSteps = (completedTransactionsCount: string) => [
  {
    title: "Catalog Analysis",
    content: `
    We provide assessments based on actual sale data from over ${completedTransactionsCount} deals, ranging from global hits to up-and-coming indie artists. By providing us with some information, we can offer a "Median Comparable Deal Price" to inform your listing strategy and next steps.`,
    hasLink: true,
  },
  {
    title: "Structure Your Offering",
    content:
      "Decide which part of your catalog you want to work with. Whether it's a select number of songs, a percentage of earnings, or a single royalty stream, we offer flexibility to match your goals. Once the structure is set, we build and market the listing to buyers.",
    hasLink: false,
  },
  {
    title: "Field Offers",
    content:
      "We guide you on how to interact with the deal page and share strategies to help you secure the highest price for your catalog. You can choose to review offers independently or have a dedicated Deal Manager assist you throughout the process, keeping you informed and answering your questions.",
    hasLink: false,
  },
  {
    title: "Closing",
    content:
      "If you don't see an offer you like, you are not obligated to accept it. When you do receive an offer you are happy with, you can accept it within 72 hours. This locks the buyer into the deal and initiates the closing process. We take care of the entire closing process on your behalf, collecting payment from the buyer and handling the royalty assignment process from start to finish, ensuring a seamless experience.",
    hasLink: false,
  },
];

const AboutDealOptions: FC = ({}) => {
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const classes = useStyles({ isXsDown, isSmDown, isMdDown, isMdUp });
  const completedTransactionsCount =
    useAppConfigs(["COMPLETED_TRANSACTIONS_COUNT"]).data
      ?.COMPLETED_TRANSACTIONS_COUNT || EMPTY_VALUE_PLACEHOLDER;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // for a smooth scroll
    });
  };

  return (
    <BaseContainer>
      <Box mt={1}>
        <Box style={{ textAlign: "left" }}>
          <Button
            className={classes.backToDashboardButton}
            variant="text"
            startIcon={<ArrowBackIcon />}
            size="large"
            component={Link}
            to={routes.sellerDashboard()}
          >
            Back to Dashboard
          </Button>
        </Box>
        <Box className={classes.header}>
          <Typography variant="h1">About Deal Options</Typography>
          <Typography variant="h2" className={classes.subtitle}>
            92% of deals close within 30 days.
          </Typography>
        </Box>
        <Grid container spacing={3} justify="center">
          {cardsContent.map((card, index) => (
            <Grid
              item
              key={index}
              style={{
                order: isSmDown ? cardsContent.length - index : 0,
                maxWidth: isSmDown && !isXsDown ? "80%" : "320px",
              }}
              xs={12}
              md={4}
            >
              <Box
                className={`${classes.card} ${
                  card.title === "Sale" ? classes.popularBorder : ""
                }`}
              >
                <Box className={classes.popularText}>Popular</Box>

                <Box mb={5} className={classes.cardInfos}>
                  <Typography variant="h5" className={classes.cardTitle}>
                    {card.title}
                  </Typography>
                  <Box className={classes.cardInfoText}>
                    <Typography>{card.subTitle}</Typography>
                  </Box>
                </Box>
                <Divider />
                <Box display="flex" flexDirection="column" mb={1} mt={5}>
                  {card.bullets.map((bullet, index) => (
                    <Box
                      key={index}
                      display="flex"
                      mt={1}
                      alignItems="center"
                      textAlign="left"
                    >
                      <CheckCircleOutlineIcon
                        color="primary"
                        className={classes.circleIcon}
                      />
                      <Typography variant="body2" style={{ marginLeft: 8 }}>
                        {bullet}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box mt={5}>
          <Box>
            <Typography variant="h1">How it Works</Typography>
            <Typography>
              <br />
              The Exchange is designed to facilitate catalog deals with
              certainty. By attracting tens of thousands of vetted buyers we
              create the most amount of competition and the highest possible
              prices.
              <br />
              <br />
              Unlike traditional catalog deals that can take months to explore
              and may still fall through, you get a simple process with a team
              to guide you.
              <br />
              <br />
              First, we work together to review potential deal options. Then, we
              bring it to thousands of buyers (20,000 verified). You don’t have
              to accept an offer, but if you do, you receive the proceeds of the
              deal within 2 weeks.
            </Typography>
          </Box>
          <Box mt={5} mb={5}>
            <Grid container>
              {makeSteps(completedTransactionsCount).map((step, index) => (
                <Grid item className={classes.stepCard} key={index}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    className={classes.cardTitle}
                  >
                    Step {index + 1}: {step.title}
                  </Typography>
                  <Box mt={3} mb={3}>
                    <Divider />
                  </Box>
                  <Typography variant="body1" paragraph>
                    {step.content}
                  </Typography>
                  {step.hasLink && (
                    <Box display="flex" justifyContent="end">
                      <Link
                        to={routes.sellerAddRoyaltyAccount()}
                        rel="noopener noreferrer"
                        className={classes.link}
                      >
                        Add Your Royalty Accounts Now{" "}
                        <ArrowForwardIosIcon
                          className={`${classes.arrowIcon} nextArrow`}
                        />
                      </Link>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
            {isXsDown && (
              <Button fullWidth variant="outlined" onClick={scrollToTop}>
                Back to Top
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </BaseContainer>
  );
};

export default AboutDealOptions;
